import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { Button, Icon, Popup } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { useIdeaIdState } from "components/ideas/hooks/useIdea";

type FollowButtonProps = {
  forType: string;
  forId: string;
  onFollow: () => void;
  onUnfollow: () => void;
  tooltip: string;
};

const FollowButton = ({
  forType,
  forId,
  onFollow,
  onUnfollow,
  tooltip,
}: FollowButtonProps) => {
  const user = useAppSelector((state) => state.user);
  const followObject = util.userIsFollowingContextObject(user, forType, forId);

  const dispatch = useAppDispatch();
  const updateUser = useCallback(
    (updatedUser: UserMe) => dispatch(actions.user.receiveUser(updatedUser)),
    [dispatch],
  );
  const { t } = useTranslation();

  return (
    <Popup
      on="hover"
      trigger={
        <div>
          {followObject.following ? (
            <Button
              size="tiny"
              secondary
              onClick={() => {
                if (!onUnfollow) {
                  return;
                }
                const updatedFollowObject = {
                  ...followObject,
                  following: false,
                  unfollowed: true,
                };

                const oldFollowing = (user?.following || []).filter(
                  (f) => f._id !== forId,
                );
                const newFollowing = [updatedFollowObject, ...oldFollowing];

                if (user) {
                  updateUser({ ...user, following: newFollowing });
                }
                onUnfollow();
              }}
            >
              <Icon name="star" />
              {t("users.following.title")}
            </Button>
          ) : (
            <Button
              size="tiny"
              basic
              inverted
              onClick={() => {
                if (!onFollow) {
                  return;
                }
                const updatedFollowObject = {
                  ...followObject,
                  following: true,
                  unfollowed: false,
                };

                const oldFollowing = (user?.following || []).filter(
                  (f) => f._id !== forId,
                );
                const newFollowing = [updatedFollowObject, ...oldFollowing];

                if (user) {
                  updateUser({ ...user, following: newFollowing });
                }
                onFollow();
              }}
            >
              <Icon name="star outline" />
              {t("users.following.follow")}
            </Button>
          )}
        </div>
      }
      content={tooltip}
    />
  );
};

type ChallengeFollowButtonProps = { challenge: OpenAPI.Schemas["Challenge"] };

export const ChallengeFollowButton = ({
  challenge,
}: ChallengeFollowButtonProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const followChallenge = useCallback(() => {
    api.challenges.follow(
      challenge._id,
      () => {
        toast.success(
          `${t("common:capitalise", { key: "generic.challenge" })} followed`,
        );
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [challenge, t]);

  const unfollowChallenge = useCallback(() => {
    api.challenges.unfollow(
      challenge._id,
      () => {
        toast(
          `${t("common:capitalise", { key: "generic.challenge" })} unfollowed`,
        );
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [challenge, t]);

  if (!user) return null;
  return (
    <FollowButton
      forType="challenge"
      forId={challenge._id}
      onFollow={followChallenge}
      onUnfollow={unfollowChallenge}
      tooltip={t("users.following.unfollowInfo")}
    />
  );
};

export const IdeaFollowButton = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [ideaId] = useIdeaIdState();

  const followIdea = useCallback(() => {
    api.ideas.follow(
      ideaId,
      () => {
        toast.success(
          `${t("common:capitalise", { key: "generic.idea" })} followed`,
        );
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [ideaId, t]);

  const unfollowIdea = useCallback(() => {
    api.ideas.unfollow(
      ideaId,
      () => {
        toast(`${t("common:capitalise", { key: "generic.idea" })} unfollowed`);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [ideaId, t]);

  if (!user) return null;

  return (
    <FollowButton
      forType="idea"
      forId={ideaId}
      onFollow={followIdea}
      onUnfollow={unfollowIdea}
      tooltip={`Follow ${t("generic.ideaWithArticle")} to receive notifications when somebody likes it or posts a comment. When creating or interacting with ${t("generic.ideaWithArticle")} you will automatically start following it.`}
    />
  );
};

type GroupFollowButtonProps = { group: OpenAPI.Schemas["Group"] };

export const GroupFollowButton = ({ group }: GroupFollowButtonProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const followGroup = useCallback(() => {
    api.groups.follow(
      group._id,
      () => {
        toast.success(`Group followed`);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [group]);

  const unfollowGroup = useCallback(() => {
    api.groups.unfollow(
      group._id,
      () => {
        toast(`Group unfollowed`);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [group]);

  if (!user) return null;

  return (
    <FollowButton
      forType="group"
      forId={group._id}
      onFollow={followGroup}
      onUnfollow={unfollowGroup}
      tooltip={`Follow groups to receive notifications when there is activity in the group, such as new feed posts, discussion posts or ${t("generic.ideas")} submitted to related ${t("generic.challenges")}. When creating or interacting with groups you will automatically start following them.`}
    />
  );
};
